// Shared goal-profile option constants. Values mirror the onboarding steps
// (Step1SubjectGrade / Step2GoalType / Step3Preferences) so My Goal stays consistent.

export const GRADE_BANDS = [
  { value: 'GRADE_1',              label: 'Grade 1' },
  { value: 'GRADE_2',              label: 'Grade 2' },
  { value: 'GRADE_3',              label: 'Grade 3' },
  { value: 'GRADE_4',              label: 'Grade 4' },
  { value: 'GRADE_5',              label: 'Grade 5' },
  { value: 'GRADE_6',              label: 'Grade 6' },
  { value: 'GRADE_7',              label: 'Grade 7' },
  { value: 'GRADE_8',              label: 'Grade 8' },
  { value: 'GRADE_9',              label: 'Grade 9' },
  { value: 'GRADE_10',             label: 'Grade 10' },
  { value: 'GRADE_11',             label: 'Grade 11' },
  { value: 'GRADE_12',             label: 'Grade 12' },
  { value: 'UNDERGRADUATE',        label: 'Undergraduate' },
  { value: 'POSTGRADUATE',         label: 'Postgraduate' },
  { value: 'WORKING_PROFESSIONAL', label: 'Working Professional' },
  { value: 'OTHER',                label: 'Other' },
] as const;

// Display-label mirror of the teaching-modes value catalog (ONLINE / OFFLINE /
// HYBRID). Selection UIs fetch the live catalog via useTeachingModes(); this
// static list only backs modeLabel() for read-only display, mirroring how
// gradeLabel/GRADE_BANDS work.
export const MODE_OPTIONS = [
  { value: 'ONLINE', label: 'Online' },
  { value: 'OFFLINE', label: 'Offline' },
  { value: 'HYBRID', label: 'Hybrid' },
] as const;

export const TIMELINE_OPTIONS = [
  { value: 'THIS_WEEK', label: 'This week' },
  { value: 'THIS_MONTH', label: 'This month' },
  { value: 'LATER', label: 'Later' },
] as const;

export const GOAL_TYPES_BY_GRADE: Record<string, string[]> = {
  GRADE_1:              ['Homework support', 'Struggling to keep up', 'Challenge them', 'Scholarship prep'],
  GRADE_2:              ['Homework support', 'Struggling to keep up', 'Challenge them', 'Scholarship prep'],
  GRADE_3:              ['Homework support', 'Struggling to keep up', 'Challenge them', 'Scholarship prep'],
  GRADE_4:              ['Homework support', 'Struggling to keep up', 'Challenge them', 'Scholarship prep'],
  GRADE_5:              ['Homework support', 'Struggling to keep up', 'Challenge them', 'Scholarship prep'],
  GRADE_6:              ['Homework support', 'Struggling to keep up', 'Challenge them', 'Competitive exam foundation'],
  GRADE_7:              ['Homework support', 'Struggling to keep up', 'Challenge them', 'Competitive exam foundation'],
  GRADE_8:              ['Homework support', 'Struggling to keep up', 'Concept clarity', 'Competitive exam foundation'],
  GRADE_9:              ['Board exam prep', 'Catch-up tuition', 'Concept clarity', 'Competitive exam foundation'],
  GRADE_10:             ['Board exam prep', 'Catch-up tuition', 'Concept clarity', 'Competitive exam foundation'],
  GRADE_11:             ['Board exam scoring', 'JEE / NEET prep', 'SAT / IELTS prep', 'University application support'],
  GRADE_12:             ['Board exam scoring', 'JEE / NEET prep', 'SAT / IELTS prep', 'University application support'],
  UNDERGRADUATE:        ['Degree coursework', 'Research support', 'Competitive exam prep', 'Upskilling'],
  POSTGRADUATE:         ['Thesis / dissertation support', 'Research guidance', 'Competitive exam prep', 'Upskilling'],
  WORKING_PROFESSIONAL: ['Upskilling', 'Career switch', 'Certification prep', 'Language learning'],
  OTHER:                ['Homework support', 'Concept clarity', 'Upskilling', 'Exam prep'],
};
export const ALL_GOAL_TYPES = [...new Set(Object.values(GOAL_TYPES_BY_GRADE).flat())];


function labelFrom(opts: ReadonlyArray<{ value: string; label: string }>, value?: string | null): string {
  if (!value) return '';
  return opts.find((o) => o.value === value)?.label ?? value;
}
export const gradeLabel = (v?: string | null) => labelFrom(GRADE_BANDS, v);
export const modeLabel = (v?: string | null) => labelFrom(MODE_OPTIONS, v);
export const timelineLabel = (v?: string | null) => labelFrom(TIMELINE_OPTIONS, v);

export function goalTypesForGrade(gradeBand?: string | null): string[] {
  if (gradeBand && GOAL_TYPES_BY_GRADE[gradeBand]) return GOAL_TYPES_BY_GRADE[gradeBand];
  return ALL_GOAL_TYPES;
}

// --- Enrichment options (availability, learning style, secondary subjects) ---
export const LEARNING_STYLES = [
  { value: 'STRUCTURED', label: 'Structured' },
  { value: 'CONCEPT_FIRST', label: 'Concept-first' },
  { value: 'PRACTICE_HEAVY', label: 'Practice-heavy' },
  { value: 'ADAPTIVE', label: 'Adaptive' },
  { value: 'CONVERSATIONAL', label: 'Conversational' },
] as const;

export const DAYS = [
  { value: 'MON', label: 'Mon' }, { value: 'TUE', label: 'Tue' }, { value: 'WED', label: 'Wed' },
  { value: 'THU', label: 'Thu' }, { value: 'FRI', label: 'Fri' }, { value: 'SAT', label: 'Sat' }, { value: 'SUN', label: 'Sun' },
] as const;

export const TIMES = [
  { value: 'MORNING', label: 'Morning' }, { value: 'AFTERNOON', label: 'Afternoon' }, { value: 'EVENING', label: 'Evening' },
] as const;

export const SUBJECT_LEVELS = [
  { value: 'BEGINNER', label: 'Beginner' }, { value: 'SOME_BASE', label: 'Some base' },
  { value: 'SOLID', label: 'Solid' }, { value: 'ADVANCED', label: 'Advanced' },
] as const;

export const SUBJECT_GOAL_TYPES = [
  { value: 'HOMEWORK', label: 'Homework help' }, { value: 'EXAM_PREP', label: 'Exam prep' },
  { value: 'ENRICHMENT', label: 'Enrichment' }, { value: 'CATCHUP', label: 'Catching up' },
] as const;

export const learningStyleLabel = (v?: string | null) => LEARNING_STYLES.find((s) => s.value === v)?.label ?? '';
